import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Skeleton,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  Chip,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { eventService, type PublicHighlightedEvent, type PublicEventTag } from '../../services/eventService'; 
import EventTagChips from '../../components/events/EventTagChips';
import { isEventEndedTag, isEventSoldOutTag } from '../../components/events/eventTagUtils';
import { platformBrand } from '../../brands/platform';
import { resolveBrand } from '../../brands/registry';
// Tipos conforme especificado pelo usuário
interface DisplayEvent {
  id: string;
  slug: string;
  title: string;
  description: string;
  startDate: Date;
  imageUrl: string;
  location?: string;
  tags: PublicEventTag[];
  parish: {
    city?: string;
    state?: string;
  };
}

/** Evita ", " quando cidade/UF vêm vazios; usa nome do local como fallback. */
function formatEventCardLocation(event: DisplayEvent): string | null {
  const city = event.parish.city?.trim();
  const state = event.parish.state?.trim();
  const cityState = [city, state].filter(Boolean).join(', ');
  if (cityState) return cityState;
  const loc = event.location?.trim();
  return loc || null;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hostBrand = resolveBrand();
  const publicBrand = hostBrand.key === 'zuadaria' ? hostBrand : platformBrand;
  const isZuadaria = publicBrand.key === 'zuadaria';
  const home = publicBrand.copy.home;
  const accentEnd = publicBrand.publicPage.ctaEnd;
  const useInstagramCarousel = isZuadaria && isMobile;
  
  // Estados para eventos conforme especificado
  const [, setEvents] = useState<DisplayEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<DisplayEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const eventsSectionRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Lógica de carregamento de eventos exatamente como solicitado
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await eventService.getHighlightedEvents();
        if (response.success && response.data) {
          const formatted = response.data.map((e: PublicHighlightedEvent): DisplayEvent => ({
            id: e._id,
            slug: e.slug,
            title: e.title,
            description: e.description ?? '',
            startDate: new Date(e.startDate),
            imageUrl: e.coverImage || home.fallbackCover,
            location: e.location,
            tags: e.tags ?? [],
            parish: {
              city: e.city,
              state: e.state
            }
          }));

          setEvents(formatted);
          setFilteredEvents(formatted);
        }
      } catch (error) {
        console.error('Erro ao buscar eventos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [home.fallbackCover]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCarouselDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const heroMosaicImages = [filteredEvents[0]?.imageUrl, filteredEvents[1]?.imageUrl, filteredEvents[2]?.imageUrl];
  const hasHeroMosaicData = heroMosaicImages.some(Boolean);
  const carouselEvents = filteredEvents.filter((e) => Boolean(e.imageUrl)).slice(0, 12);

  const handleCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el || carouselEvents.length === 0) return;
    const slide = el.querySelector('[data-carousel-slide]') as HTMLElement | null;
    if (!slide) return;
    const styles = window.getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap || '14') || 14;
    const slideWidth = slide.offsetWidth + gap;
    const next = Math.round(el.scrollLeft / Math.max(slideWidth, 1));
    setCarouselIndex(Math.max(0, Math.min(next, carouselEvents.length - 1)));
  }, [carouselEvents.length]);

  useEffect(() => {
    setCarouselIndex(0);
    if (carouselRef.current) carouselRef.current.scrollLeft = 0;
  }, [carouselEvents.length]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          py: { xs: 5, md: 12 },
          pt: { xs: 3, md: undefined },
        }}
      >
        <Box sx={{ position: 'absolute', top: -140, left: -120, width: 420, height: 420, borderRadius: '50%', background: alpha(theme.palette.primary.main, 0.16), filter: 'blur(95px)' }} />
        <Box sx={{ position: 'absolute', bottom: -200, right: -140, width: 520, height: 520, borderRadius: '50%', background: alpha('#b8abff', 0.22), filter: 'blur(110px)' }} />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} lg={5} sx={{ minWidth: 0 }}>
              <Chip
                label={home.chip}
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  letterSpacing: { xs: '0.06em', sm: '0.1em' },
                  fontSize: { xs: '0.65rem', sm: '0.8125rem' },
                  maxWidth: '100%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Sora","Manrope",sans-serif',
                  fontSize: { xs: 'clamp(1.65rem, 6.5vw, 2.4rem)', md: '4.2rem' },
                  fontWeight: 800,
                  lineHeight: 1.08,
                  color: publicBrand.publicPage.heading,
                  mb: 2,
                  wordBreak: 'break-word',
                }}
              >
                {home.title} <br />
                <Box component="span" sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${accentEnd})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {home.titleAccent}
                </Box>
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 520, fontSize: { xs: '0.95rem', sm: '1.1rem' }, mb: 4, lineHeight: 1.55 }}>
                {home.subtitle}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4, width: '100%', maxWidth: '100%' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  sx={{ minWidth: { xs: 0, sm: 180 }, py: 1.4, borderRadius: 999, fontWeight: 700 }}
                >
                  {home.ctaExplore}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate(home.ctaSecondaryHref)}
                  sx={{ minWidth: { xs: 0, sm: 170 }, py: 1.4, borderRadius: 999, borderColor: alpha(theme.palette.primary.main, 0.24) }}
                >
                  {home.ctaSecondary}
                </Button>
              </Stack>
              {/* Bloco social oculto temporariamente.
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Stack direction="row" sx={{ '& > span': { ml: -1.2 } }}>
                  {['#E8D5CC', '#DDC2B8', '#C9B3E0'].map((color) => (
                    <Box key={color} component="span" sx={{ width: 34, height: 34, borderRadius: '50%', background: color, border: '2px solid #fff' }} />
                  ))}
                  <Box component="span" sx={{ width: 34, height: 34, borderRadius: '50%', background: alpha(theme.palette.primary.main, 0.25), border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: theme.palette.primary.dark }}>+2k</Box>
                </Stack>
                <Typography variant="body2" color="text.secondary">Junte-se a milhares de participantes</Typography>
              </Stack>
              */}
            </Grid>

            <Grid item xs={12} lg={7} sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: useInstagramCarousel ? 1.25 : 1.5, sm: 2.5 },
                  borderRadius: { xs: 4, sm: 6 },
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  bgcolor: alpha('#ffffff', 0.68),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  backdropFilter: 'blur(10px)',
                  animation: useInstagramCarousel ? 'none' : 'mosaicFloat 7s ease-in-out infinite',
                  '@keyframes mosaicFloat': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-6px)' },
                  },
                }}
              >
                {useInstagramCarousel ? (
                  <Box sx={{ width: '100%' }}>
                    {loading && carouselEvents.length === 0 ? (
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        sx={{ borderRadius: 2.5, width: '100%', aspectRatio: '16 / 10' }}
                      />
                    ) : (
                      <>
                        {/* Stories progress — um único indicador */}
                        {carouselEvents.length > 1 && (
                          <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, px: 0.25 }}>
                            {carouselEvents.map((event, idx) => (
                              <Box
                                key={`bar-${event.id}`}
                                sx={{
                                  flex: 1,
                                  height: 2.5,
                                  borderRadius: 999,
                                  bgcolor: alpha(theme.palette.primary.main, idx === carouselIndex ? 1 : 0.18),
                                  transition: 'background-color 0.2s ease',
                                }}
                              />
                            ))}
                          </Stack>
                        )}

                        <Box
                          ref={carouselRef}
                          onScroll={handleCarouselScroll}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start', // nunca estica cards — evita o buraco branco
                            gap: 1.5,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            scrollSnapType: 'x mandatory',
                            WebkitOverflowScrolling: 'touch',
                            scrollBehavior: 'smooth',
                            mx: -1.25,
                            px: 1.25,
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                          }}
                        >
                          {carouselEvents.map((event) => (
                            <Box
                              key={event.id}
                              data-carousel-slide
                              role="link"
                              tabIndex={0}
                              onClick={() => navigate(`/${event.slug}`)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  navigate(`/${event.slug}`);
                                }
                              }}
                              sx={{
                                flex: '0 0 86%',
                                scrollSnapAlign: 'center',
                                scrollSnapStop: 'always',
                                borderRadius: 2.5,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                bgcolor: '#fff',
                                boxShadow: `0 8px 24px ${alpha('#1A1515', 0.12)}`,
                                border: `1px solid ${alpha('#1A1515', 0.06)}`,
                                WebkitTapHighlightColor: 'transparent',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:active': { transform: 'scale(0.985)' },
                              }}
                            >
                              {/*
                                Frame 16:10 + blur fill + contain:
                                - capa sempre inteira (sem crop estranho)
                                - sem faixa preta / buraco
                                - proporção estável de banner de evento
                              */}
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  aspectRatio: '16 / 10',
                                  overflow: 'hidden',
                                  bgcolor: '#1A1515',
                                }}
                              >
                                <Box
                                  aria-hidden
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundImage: `url(${event.imageUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    filter: 'blur(22px) saturate(1.05)',
                                    transform: 'scale(1.2)',
                                    opacity: 0.85,
                                  }}
                                />
                                <Box
                                  aria-hidden
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    background: alpha('#1A1515', 0.22),
                                  }}
                                />
                                <Box
                                  component="img"
                                  src={event.imageUrl}
                                  alt={event.title}
                                  sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    objectPosition: 'center',
                                    display: 'block',
                                    userSelect: 'none',
                                    pointerEvents: 'none',
                                  }}
                                />
                              </Box>

                              <Box sx={{ px: 1.75, pt: 1.35, pb: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 0.55 }}>
                                  <EventIcon sx={{ fontSize: 15, color: theme.palette.primary.main }} />
                                  <Typography
                                    sx={{
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      color: theme.palette.primary.main,
                                      letterSpacing: '0.01em',
                                    }}
                                  >
                                    {formatCarouselDate(event.startDate)}
                                  </Typography>
                                </Stack>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: '0.98rem',
                                    lineHeight: 1.28,
                                    color: '#1A1515',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {event.title}
                                </Typography>
                              </Box>
                            </Box>
                          ))}

                          {carouselEvents.length === 0 && !loading && (
                            <Box
                              sx={{
                                flex: '0 0 100%',
                                borderRadius: 2.5,
                                py: 5,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 1,
                                color: theme.palette.primary.dark,
                              }}
                            >
                              <EventIcon />
                              <Typography fontWeight={700}>{home.emptyTitle}</Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                ) : (
                <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ width: '100%' }}>
                  <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
                    {loading && !hasHeroMosaicData ? (
                      <Skeleton variant="rounded" animation="wave" sx={{ borderRadius: 4, minHeight: { xs: 240, md: 410 } }} />
                    ) : (
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 4,
                          overflow: 'hidden',
                          width: '100%',
                          maxWidth: '100%',
                          minHeight: { xs: 240, md: 410 },
                          backgroundImage: heroMosaicImages[0] ? `url(${heroMosaicImages[0]})` : 'none',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          animation: 'mosaicReveal 480ms ease-out both, tilePulse 9s ease-in-out infinite',
                          '@keyframes mosaicReveal': {
                            from: { opacity: 0, transform: 'translateY(14px) scale(0.985)' },
                            to: { opacity: 1, transform: 'translateY(0) scale(1)' },
                          },
                          '@keyframes tilePulse': {
                            '0%, 100%': { filter: 'saturate(100%)' },
                            '50%': { filter: 'saturate(108%)' },
                          },
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
                    <Stack spacing={{ xs: 1, sm: 1.5 }} sx={{ height: '100%', minWidth: 0 }}>
                      {loading && !hasHeroMosaicData ? (
                        <Skeleton variant="rounded" animation="wave" sx={{ borderRadius: 4, minHeight: { xs: 140, md: 198 } }} />
                      ) : (
                        <Box
                          sx={{
                            flex: 1,
                            borderRadius: 4,
                            overflow: 'hidden',
                            width: '100%',
                            minWidth: 0,
                            minHeight: { xs: 140, md: 198 },
                            backgroundImage: heroMosaicImages[1] ? `url(${heroMosaicImages[1]})` : 'none',
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            animation: 'mosaicReveal 620ms ease-out both',
                          }}
                        />
                      )}
                      <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} sx={{ flex: 1, minWidth: 0 }}>
                        {loading && !hasHeroMosaicData ? (
                          <Skeleton variant="rounded" animation="wave" sx={{ flex: 1, minWidth: 0, borderRadius: 4, minHeight: { xs: 140, md: 198 } }} />
                        ) : (
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              borderRadius: 4,
                              overflow: 'hidden',
                              minHeight: { xs: 140, md: 198 },
                              backgroundImage: heroMosaicImages[2] ? `url(${heroMosaicImages[2]})` : 'none',
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              animation: 'mosaicReveal 760ms ease-out both',
                            }}
                          />
                        )}
                        <Box
                          onClick={() => eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            borderRadius: 4,
                            minHeight: { xs: 140, md: 198 },
                            bgcolor: alpha(theme.palette.primary.main, 0.18),
                            color: theme.palette.primary.dark,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            animation: 'mosaicReveal 900ms ease-out both, ctaBreathe 2.8s ease-in-out infinite',
                            '@keyframes ctaBreathe': {
                              '0%, 100%': { boxShadow: '0 0 0 0 rgba(75, 27, 122, 0.0)' },
                              '50%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.10)}` },
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.24),
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <EventIcon sx={{ mb: 0.6, fontSize: { xs: 22, sm: 24 } }} />
                          <Typography component="span" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, textAlign: 'center', px: 0.5 }}>
                            {Math.max(filteredEvents.length, 1)}+ {home.eventsCountLabel}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Seção de Eventos em Destaque */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, sm: 3 } }} ref={eventsSectionRef}>
        <Box textAlign="center" mb={{ xs: 5, md: 8 }}>
          <Chip 
            label={home.eventsChip} 
            sx={{ 
              mb: 3,
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              maxWidth: '100%',
            }} 
          />
          <Typography 
            variant="h2" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            sx={{
              fontSize: { xs: '1.65rem', sm: '2rem', md: '2.75rem' },
              background: 'linear-gradient(45deg, #1f2937, #374151)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3,
              px: { xs: 0.5, sm: 0 },
            }}
          >
            {home.eventsTitle}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontSize: { xs: '0.95rem', sm: '1.25rem' }, px: { xs: 0.5, sm: 0 } }}>
            {home.eventsSubtitle}
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={{ xs: 2.5, md: 4 }}>
              {(showAllEvents ? filteredEvents : filteredEvents.slice(0, 6)).map((event) => {
                const locationLabel = formatEventCardLocation(event);
                const ended = isEventEndedTag(event.tags);
                const soldOut = isEventSoldOutTag(event.tags);
                return (
                <Grid item xs={12} md={4} key={event.id} sx={{ minWidth: 0 }}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      border: '1px solid #d8e0ed',
                      opacity: ended ? 0.82 : soldOut ? 0.94 : 1,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.22)}`,
                        borderColor: theme.palette.primary.main
                      }
                    }}
                    onClick={() => navigate(`/${event.slug}`)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <EventTagChips tags={event.tags} overlay compact />
                      <CardMedia
                        component="img"
                        image={event.imageUrl}
                        alt={event.title}
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                          height: { xs: 180, sm: 200 },
                          objectFit: 'cover',
                          display: 'block',
                          filter: ended ? 'grayscale(0.85) brightness(0.92)' : soldOut ? 'saturate(0.75)' : 'none',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <EventIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(event.startDate)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="h6" component="h3" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                        {event.title}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {event.description}
                      </Typography>
                      
                      {locationLabel ? (
                        <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                          <LocationOnIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, mr: 0.5, flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary" noWrap title={locationLabel}>
                            {locationLabel}
                          </Typography>
                        </Box>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
              );
              })}
            </Grid>

            {filteredEvents.length > 6 && (
              <Box textAlign="center" mt={6}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    const wasShowingAll = showAllEvents;
                    setShowAllEvents(!showAllEvents);
                    // Se estava mostrando menos e agora vai mostrar todos, fazer scroll suave
                    if (!wasShowingAll && eventsSectionRef.current) {
                      setTimeout(() => {
                        eventsSectionRef.current?.scrollIntoView({ 
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }, 100);
                    }
                  }}
                  sx={{ 
                    px: 6,
                    py: 1.5,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      background: alpha(theme.palette.primary.main, 0.07)
                    }
                  }}
                >
                  {showAllEvents ? 'Ver Menos Eventos' : 'Ver Todos os Eventos'}
                </Button>
              </Box>
            )}

            {filteredEvents.length === 0 && !loading && (
              <Paper 
                elevation={0}
                sx={{ 
                  p: 6, 
                  textAlign: 'center',
                  background: alpha(theme.palette.primary.main, 0.07),
                  border: `2px dashed ${alpha(theme.palette.primary.main, 0.26)}`
                }}
              >
                <EventIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2, opacity: 0.7 }} />
                <Typography variant="h6" gutterBottom>
                  {home.emptyTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {home.emptySubtitle}
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default HomePage;

